// HUD state: one place that turns race snapshots and events into what the panels show.
// Updated by the app loop through update(); panels read the signals.
import { signal } from '@preact/signals';
import { CAT_IDS, type CatId, type RaceEvent, type RaceSnapshot } from '../race/types';
import { CAT_DISPLAY } from './cats';
import { formatDelta, formatTime, ordinal } from './format';
import { applyRace, createBet, deleteBet, loadBets, pauseBet, resumeBet, saveBets, type Bet, type BetDraft, type BetOutcome } from './bets';
import { appendHistory, clearHistory, loadHistory, RaceRecorder, type RaceRecord } from './history';

export type SourceKind = 'live' | 'replay';
export type Connection = 'connecting' | 'open' | 'reconnecting' | 'failed';
export type CameraChoice = 'director' | 'follow' | 'leader' | 'overview' | 'finish';

export interface Caption { id: number; text: string; kind: 'info' | 'finish' | 'catnip' | 'lead'; until: number }
export interface SourceInfo { kind: SourceKind; label: string; connection: Connection }

/** What the top badge says. Recorded races always say so. */
export interface Badge { tone: 'live' | 'replay' | 'warn' | 'lost'; text: string; detail?: string; title?: string }

const CAPTION_MS = 4000;
/** How long a bet's race outcome stays in the ticker. */
const BET_NEWS_MS = 15_000;
const VOLUME_KEY = 'kd-volume';

function loadVolume(): number {
  try {
    const v = parseFloat(globalThis.localStorage?.getItem(VOLUME_KEY) ?? '');
    return Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0.8;
  } catch { return 0.8; }
}
const SNAPSHOT_INTERVAL_MS = 100;

export function name(id: CatId): string { return CAT_DISPLAY[id].short; }

type CaptionText = Omit<Caption, 'id' | 'until'>;

// The ticker lines of kissaderby.fi/modern (.taiga/knowledge/landscape/kissaderby-modern-sound.md),
// one picked at random per event. {cat} is the cat's full name.
const LINES = {
  starting: ['Post time. Six cats, one ham, an undisclosed amount of catnip.', 'The cats are in the boxes. The boxes are unhappy about it.', 'Welcome back to the derby. Weather is beautiful, somewhere.'],
  running: ['And they are off!', 'Go go go. Or whatever cats do.', 'Racing! Try to keep up.'],
  hamDrop: ['A ham has appeared in lane {lane}. {cat} smells it.', 'Ham drop. {cat}, this one is yours if you want it.'],
  ham: ['{cat} found the ham. Kinkkukiri!', 'Ham for {cat}. Nobody is riding a hare here.', '{cat} stops for a snack. The audience approves.'],
  catnip: ['{cat} is on the catnip. Doping control has been notified.', '{cat} sees colours that do not exist. Tripping.', 'Catnip. {cat} is now somewhere else entirely.'],
  lead: ['{cat} takes the lead. Johtoon!', 'Lead change: {cat} in front now.', '{cat} goes to the front like a bear that has been shot in the ass.'],
  finish: ['{cat} crosses the line. {word}!', '{cat} is home in {ordinal}.'],
  wins: ['There are two ends to a sausage.', 'Rahikainen has the same problem.', "Comes loose like grandma's tooth.", 'His moped starts doing wheelies.'],
};
const PLACE_WORDS = ['voittoon', 'toka', 'kolmas', 'neljas', 'viides', 'vika'];

/** Caption lines for an event (none, one or two), in the original page's words where it has them. */
export function captionFor(e: RaceEvent, snap: RaceSnapshot | null, rand: () => number = Math.random): CaptionText[] {
  const pick = (lines: readonly string[], cat?: CatId, vars: Record<string, string> = {}) => {
    let text = lines[Math.floor(rand() * lines.length)] ?? lines[0]!;
    if (cat) text = text.replace(/\{cat\}/g, CAT_DISPLAY[cat].name);
    for (const [k, v] of Object.entries(vars)) text = text.replace(`{${k}}`, v);
    return text;
  };
  switch (e.type) {
    case 'lead-change': return [{ kind: 'lead', text: pick(LINES.lead, e.leader) }];
    case 'ham-start': return [{ kind: 'info', text: pick(LINES.ham, e.cat) }];
    case 'ham-moved': {
      if (snap?.phase !== 'running' || rand() >= 0.35) return [];
      const cat = CAT_IDS[e.lane];
      return cat ? [{ kind: 'info', text: pick(LINES.hamDrop, cat, { lane: String(e.lane + 1) }) }] : [];
    }
    case 'catnip-catch': return [{ kind: 'catnip', text: pick(LINES.catnip, e.cat) }];
    case 'catnip-landing': {
      const forward = e.delta > 0;
      return [{ kind: 'catnip', text: `${name(e.cat)} lands ${formatDelta(e.delta)}${forward ? ' · doping control notified' : ''}` }];
    }
    case 'finish':
      return [{ kind: 'finish', text: pick(LINES.finish, e.cat, { word: PLACE_WORDS[e.place - 1] ?? ordinal(e.place), ordinal: ordinal(e.place) }) }];
    case 'close-finish': return [{ kind: 'finish', text: `Photo finish: ${name(e.cats[0])} and ${name(e.cats[1])}` }];
    case 'phase':
      if (e.phase === 'starting') return [{ kind: 'info', text: pick(LINES.starting) }];
      if (e.phase === 'running') return [{ kind: 'info', text: pick(LINES.running) }];
      return [];
    case 'results': {
      const ranked = [...e.results].sort((a, b) => a.place - b.place);
      const w = ranked[0];
      if (!w) return [];
      const out: CaptionText[] = [{ kind: 'finish', text: `${CAT_DISPLAY[w.id].name} wins in ${formatTime(w.seconds)}. ${pick(LINES.wins)}` }];
      const dsq = ranked.filter((r) => r.dsq);
      if (dsq.length) out.push({ kind: 'catnip', text: `Doping control: ${dsq.map((r) => CAT_DISPLAY[r.id].name).join(', ')} disqualified. Catnip.` });
      return out;
    }
    default: return [];
  }
}

export function badgeFor(src: SourceInfo, snap: RaceSnapshot | null): Badge {
  if (src.kind === 'replay') {
    return { tone: 'replay', text: 'REPLAY · recorded race', detail: 'Not the live race', title: src.label || undefined };
  }
  if (snap?.signal === 'lost' || src.connection === 'failed') return { tone: 'lost', text: 'Signal lost — reconnecting to kissaderby.fi' };
  if (src.connection === 'reconnecting' || snap?.signal === 'thin') return { tone: 'warn', text: 'Reconnecting…' };
  if (src.connection === 'connecting' && !snap) return { tone: 'warn', text: 'Connecting to the derby…' };
  if (snap?.phase === 'starting') return { tone: 'live', text: 'LIVE · at the gates' };
  if (snap?.joinedMidRace && snap.phase === 'running') return { tone: 'live', text: 'LIVE · joined mid-race' };
  return { tone: 'live', text: 'LIVE · kissaderby.fi' };
}

export class HudStore {
  readonly snapshot = signal<RaceSnapshot | null>(null);
  readonly source = signal<SourceInfo>({ kind: 'live', label: '', connection: 'connecting' });
  readonly captions = signal<Caption[]>([]);
  /** Provisional finish places from 'finish' events, until official results arrive. */
  readonly finishes = signal<Partial<Record<CatId, number>>>({});
  readonly followed = signal<CatId | null>(null);
  readonly camera = signal<CameraChoice>('director');
  readonly cardOpen = signal<CatId | null>(null);
  readonly muted = signal(true);
  /** Master sound volume 0..1, remembered like the original page does (localStorage 'kd-volume'). */
  readonly volume = signal(loadVolume());
  readonly restrained = signal(false);
  readonly started = signal(false);
  readonly loadProgress = signal<number | null>(null);
  /** Live races watched to the end in this browser, oldest first. */
  readonly history = signal<RaceRecord[]>(loadHistory());
  readonly historyOpen = signal(false);
  /** Bets between viewers, saved in this browser. */
  readonly bets = signal<Bet[]>(loadBets());
  readonly betsOpen = signal(false);
  /** What the latest race did to each bet, shown in the ticker until `until` (page time, ms). */
  readonly betNews = signal<(BetOutcome & { until: number })[]>([]);
  private nowMs = 0;
  private recorder = new RaceRecorder((r) => {
    this.history.value = appendHistory(this.history.value, r);
    const { bets, outcomes } = applyRace(this.bets.value, r);
    if (!outcomes.length) return;
    this.setBets(bets);
    this.betNews.value = outcomes.map((o) => ({ ...o, until: this.nowMs + BET_NEWS_MS }));
  });

  private nextId = 1;

  /** rand picks among the original caption lines; seeded with ?seed. */
  constructor(private rand: () => number = Math.random) {}

  setVolume(v: number): void {
    this.volume.value = Math.min(1, Math.max(0, v));
    try { localStorage.setItem(VOLUME_KEY, String(this.volume.value)); } catch { /* storage blocked */ }
  }
  private lastSnapAt = -Infinity;
  private raceNo = -1;

  /** Called each frame; cheap when nothing changed. */
  update(snap: RaceSnapshot, events: RaceEvent[], nowMs: number, src?: SourceInfo): void {
    if (src) {
      const cur = this.source.value;
      if (cur.kind !== src.kind || cur.label !== src.label || cur.connection !== src.connection) this.source.value = { ...src };
    }
    this.nowMs = nowMs;
    if (this.betNews.value.length && this.betNews.value.every((n) => n.until <= nowMs)) this.betNews.value = [];
    this.recorder.update(snap, events, (src ?? this.source.value).kind === 'live' && snap.source === 'live');
    if (snap.raceNo !== this.raceNo) { this.raceNo = snap.raceNo; this.finishes.value = {}; }
    if (events.length || nowMs - this.lastSnapAt >= SNAPSHOT_INTERVAL_MS) {
      this.snapshot.value = snap;
      this.lastSnapAt = nowMs;
    }
    let caps = this.captions.value.filter((c) => c.until > nowMs);
    for (const e of events) {
      if (e.type === 'finish') this.finishes.value = { ...this.finishes.value, [e.cat]: e.place };
      if (e.type === 'phase' && e.phase === 'starting') this.finishes.value = {};
      for (const c of captionFor(e, snap, this.rand)) caps = [...caps, { ...c, id: this.nextId++, until: nowMs + CAPTION_MS }].slice(-3);
    }
    if (caps.length !== this.captions.value.length || caps.some((c, i) => c !== this.captions.value[i])) this.captions.value = caps;
  }

  /** Short status for a standings row: icon and text, never colour alone. */
  static status(c: RaceSnapshot['cats'][number], snap: RaceSnapshot, provisional?: number): { icon: string; text: string } {
    const r = snap.results?.find((x) => x.id === c.id);
    if (r?.dsq) return { icon: '⛔', text: 'DSQ' };
    if (r) return { icon: '🏁', text: `Finished ${ordinal(r.place)}` };
    if (c.finished) return { icon: '🏁', text: `Finished ${ordinal(c.finishPlace ?? provisional ?? c.place)}` };
    if (c.tripping) return { icon: '🌀', text: 'Catnip' };
    if (c.eating) return { icon: '🍖', text: 'Ham' };
    return { icon: '🐾', text: 'Running' };
  }

  clearHistory(): void { this.history.value = clearHistory(); }

  private setBets(bets: Bet[]): void { this.bets.value = bets; saveBets(bets); }
  addBet(d: BetDraft, now = Date.now()): Bet {
    const bet = createBet(d, now, name);
    this.setBets([...this.bets.value, bet]);
    return bet;
  }
  pauseBet(id: string): void { this.setBets(pauseBet(this.bets.value, id)); }
  resumeBet(id: string, now = Date.now()): void { this.setBets(resumeBet(this.bets.value, id, now)); }
  deleteBet(id: string): void { this.setBets(deleteBet(this.bets.value, id)); }

  /** Opens one of the two big panels (bets or history) and closes the other. */
  togglePanel(which: 'bets' | 'history'): void {
    const open = which === 'bets' ? !this.betsOpen.value : !this.historyOpen.value;
    this.betsOpen.value = which === 'bets' && open;
    this.historyOpen.value = which === 'history' && open;
  }

  follow(id: CatId | null): void {
    this.followed.value = id;
    this.camera.value = id ? 'follow' : 'director';
  }
}
